import React from 'react';
import { Helmet } from 'react-helmet-async';

// Helper function to get the base URL dynamically
const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://truckaccidentsupport.com';
};

export const OrganizationSchema = () => {
  const baseUrl = getBaseUrl();
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "TruckAccidentSupport.com",
    "url": baseUrl,
    "logo": `${baseUrl}/logo.png`,
    "description": "Professional guidance for truck drivers, fleet owners, and safety managers on accident prevention, driver protection, and legal risk reduction.",
    "sameAs": [
      // Add social media links when available
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "info@truckaccidentsupport.com",
      "contactType": "Customer Service",
      "areaServed": "US"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const WebsiteSchema = () => {
  const baseUrl = getBaseUrl();
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "TruckAccidentSupport.com",
    "url": baseUrl,
    "description": "Truck accident prevention, safety training, and legal protection resources for the trucking industry.",
    "publisher": {
      "@type": "Organization",
      "name": "TruckAccidentSupport.com"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${baseUrl}/blog?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const BlogPostSchema = ({ post }) => {
  const baseUrl = getBaseUrl();
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": {
      "@type": "Organization",
      "name": "TruckAccidentSupport.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "TruckAccidentSupport.com",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${baseUrl}/blog/${post.id}`
    },
    "articleSection": post.category
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const VideoSchema = ({ video }) => {
  const baseUrl = getBaseUrl();
  const schema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": video.title,
    "description": video.description,
    "thumbnailUrl": video.thumbnail,
    "uploadDate": video.created_at || new Date().toISOString(),
    "duration": `PT${video.duration.replace(':', 'M')}S`,
    "contentUrl": video.video_url || `${baseUrl}/videos/${video.id}`,
    "embedUrl": video.video_url,
    "publisher": {
      "@type": "Organization",
      "name": "TruckAccidentSupport.com",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const BreadcrumbSchema = ({ items }) => {
  const baseUrl = getBaseUrl();
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${baseUrl}${item.path}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};
